import React, {forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState} from "react";
import {Box, Button, Checkbox, Chip, Grid, IconButton, Menu, MenuItem, Typography, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {AddRounded, DeleteRounded, UploadRounded, MoreVertRounded, VisibilityRounded} from "@mui/icons-material";
import {useFieldArray, useForm, FormProvider} from "react-hook-form";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {yupResolver} from "@hookform/resolvers/yup";
import {useMutation} from "@tanstack/react-query";
import {isMobile} from "react-device-detect";
import DamageSelector from "../damage-selector/DamageSelector";
import {EVENT_CARD_FORM_INITIAL_VALUES, EVENT_CARD_FORM_VALIDATION_SCHEMA, MAX_NUM_DAMAGE_ATTACH_FILES} from "../../config";
import Preview from "../../../preview/Preview";
import {IDamage, IDamageListComponent, IEvent, IEventCardForm} from "../../../models/models";
import {checkOutInBufferDataActionTypeState, checkOutInEventsListState, checkOutInModeState, checkVehicleStatusState} from "../../state";
import EventSelector from "../event-selector/EventSelector";
import {FormDateTimePicker, FormTextField} from "../../../common/form-fields-wrappers";
import {fetchCheckOutInGetAttachmentBlogById, fetchCheckOutInGetSnapshotBlogById} from "../../services/http.services";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {deepClone} from "../../../../../util/state/commonFunctions";
import CustomLoader from "../../../../../util/component/CustomLoader";
import {resizeImg} from "../../../utils";
import { usePartLabel } from "./translations";
import useOperationTranslation from "../../../../operation/hooks/useOperationTranslation";

const DamageList = forwardRef(({damages, eventList, readOnly = false}: IDamageListComponent, ref: any) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network', 'translation']);
	const { t: t2 } = useTranslation('operation');
	const operationTranslation = useOperationTranslation();
	const getPartLabel = usePartLabel();

	const {dispatchGlobalData} = useContext(GlobalContext);

	const checkOutInMode = useRecoilValue(checkOutInModeState);
	const eventOptions = useRecoilValue(checkOutInEventsListState);
	const checkVehicle = useRecoilValue(checkVehicleStatusState);
	const setBufferDataActionDamage = useSetRecoilState(checkOutInBufferDataActionTypeState('damage'));
	const setBufferDataActionEvent = useSetRecoilState(checkOutInBufferDataActionTypeState('event'));

	const inputTypeFilesDmgSnapshotRef: any = useRef({});
	const inputTypeFileModuleRef: any = useRef();
	let selectedEventIdUploadFile: number | null = null;
	let selectedModuleCodeUpload: 'CAI' | 'DICHIARAZIONE_CLIENTE' | null = null;

	const unavailableEvents = !!eventList ? eventList.map((event: IEvent) => event.eventName.id).filter(elem => elem !== 1) : [];

	/** events */
	// open dialog
	const [openCreateEventSelector, setOpenCreateEventSelector] = useState(false);
	// view list in create new event mode
	const [createEventMode, setCreateEventMode] = useState(false);

	// damage list to add to the event
	const [newEvent, setNewEvent]: any = useState<null | IEvent>(null);

	// Damage
	const [openDamageSelector, setOpenDamageSelector] = useState(false);
	const [damageToUpdate, setDamageToUpdate] = useState<any>();
	const [openPreviewSnapshot, setOpenPreviewSnapshot] = useState(false);
	const [openPreviewAttach, setOpenPreviewAttach] = useState(false);
	const [filePreview, setFilePreview] = useState<any>(null);

	// const [checkDamagePlatePhoto, setCheckDamagePlatePhoto] = useRecoilState<any>(checkDamageTypeState('platePhoto'));

	const eventsForm = useForm<IEventCardForm>({
		defaultValues: EVENT_CARD_FORM_INITIAL_VALUES,
		resolver: yupResolver(
			EVENT_CARD_FORM_VALIDATION_SCHEMA
		)
	});

	const getPartLabelWithTranslation = (damage: any) =>{

		console.log("damage test su list: ",damage);
		var partCode = damage?.part?.code;
		console.log("partNameList: ",partCode);
		return t2(`masterDataDamage.partName.${partCode}` as any);
	}


	const { append, remove, fields } = useFieldArray<IEventCardForm>({
		control: eventsForm.control,
		name: "card"
	});

	const [anchorEl, setAnchorEl] = useState( null);
	const [menuId, setMenuId] = useState<'evt' | 'evt-dmg' | 'dmg' | 'den' | 'cid' | null>( null);
	const [selectedDamage, setSelectedDamage] = useState(null);

	useImperativeHandle(ref, () => ({
		checkFormValidation() {
			return eventsForm.trigger();
		},

		getFormValue() {
			return eventsForm.getValues('card');
		}
	}));


	/** Events */
	// button create event (open dialog)
	const handleCreateEvent = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		append({
			eventId: '',
			counterpart: false,
			counterpartData: {
				claimForm: null,
				cid: null,
				plate: '',
				date: null,
				place: ''
			}
		});
		setOpenCreateEventSelector(true);
	}

	// init associate damage to form mode (createEventMode)
	const handleCloseEventSelector = (event: IEvent) => {
		setOpenCreateEventSelector(false);

		let eventsNum: any = eventsForm.getValues('card')?.length;
		eventsNum = !!eventsNum ? eventsNum - 1 : null;
		if (eventsNum !== undefined && eventsNum !== null && eventsNum > -1) {
			eventsForm.setValue(`card.${eventsNum}.counterpart`, true);
			if (!!event) {
				setNewEvent(event);
				setCreateEventMode(true);
			}
		}
	};

	const handleEventAssociate = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (!!newEvent && newEvent.damages.length > 0 && !readOnly) {
			setBufferDataActionEvent({
				action: !newEvent.id ? 'create' : 'associate',
				data: newEvent
			});
			setCreateEventMode(false);
		} else {
			dispatchGlobalData({
				type: 'SET_SNACKBAR',
				payload: {
					snackbar: {
						message: t('raCheckOutIn.checkDamage.message.info.selectDamageToAssociate'),
						type: snackbarTypes.info,
						show: true,
						autoClose: true,
					}
				}
			});
		}
	}

	const handleCreateEventCancel = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		setNewEvent(null);
		setCreateEventMode(false);
	}

	const handleDeleteDamageRepairSheet = (e: React.SyntheticEvent, event: IEvent) => {
		e.stopPropagation();

		if (!!event && !readOnly) {
			setBufferDataActionEvent({
				action: 'delete',
				data: event
			});
		}
	}

	/** Damage */
	const handleOpenAddDamage = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		// if (!checkDamagePlatePhoto) {
		// 	// @ts-ignore
		// 	platePhotoRef.current.click();
		// }

		setOpenDamageSelector(true);
	}

	// /** File plate photo */
	// const handlePlatePhotoOnChange = (e: React.BaseSyntheticEvent) => {
	// 	e.stopPropagation();
	//
	// 	if (!!e.target.files[0]?.size) {
	// 		let reader = new FileReader();
	// 		reader.readAsArrayBuffer(e.target.files[0]);
	//
	// 		reader.onload = function() {
	// 			setCheckDamagePlatePhoto({platePhoto: reader.result});
	// 		};
	// 	}
	// }

	const handleSelectEventDamage = (e: React.SyntheticEvent, checked: boolean, damage: IDamage) => {
		e.stopPropagation();

		// clone recoil state
		const newEv: IEvent = deepClone(newEvent);
		if (checked) { 													// add damage to event
			newEv.damages.push(damage);
		}
		else {															// remove damage from event list
			const ind = newEv.damages.findIndex((elem: IDamage) => elem.id === damage.id);
			if (ind !== -1) {
				newEv.damages.splice(ind, 1);
			}
		}
		setNewEvent(newEv);
	}

	const handleCloseAddDamage = (damage: IDamage) => {
		setDamageToUpdate(undefined);
		setOpenDamageSelector(false);
	};

	const handleDeleteDamage = (e: React.BaseSyntheticEvent,damage: IDamage) => {
		e.stopPropagation();

		if (!readOnly) {
			setBufferDataActionDamage({
				action: 'delete',
				data: damage
			});
		}
	}

	/** File */
	const handleFileDmgSnapshotOnChange = async (e: React.BaseSyntheticEvent, damage: IDamage, index: number) => {
		e.stopPropagation();

		if (!!e.target.files[0]?.size) {
			const resizedFile = await resizeImg(e.target.files[0])
			setBufferDataActionDamage({
				action: 'addSnapshot',
				data: {dmg: damage, file: resizedFile}
			});
			inputTypeFilesDmgSnapshotRef.current[index].value  = '';
		}
	}

	const handleDeleteFile = (attach: {snapshotId: string}) => {
		if (!attach?.snapshotId) {
			return;
		}

		setBufferDataActionDamage({
			action: 'delSnapshot',
			data: attach.snapshotId
		});
	}

	const fetchCheckOutInGetSnapshotBlogByIdMutation = useMutation(
		fetchCheckOutInGetSnapshotBlogById,
		{
			onSuccess: (response: any) => {
				setFilePreview({file: "data:image/png;base64," + response.img, snapshotId: response.snapshotId});
				setOpenPreviewSnapshot(true);
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	const fetchCheckOutInGetAttachmentBlogByIdMutation = useMutation(
		fetchCheckOutInGetAttachmentBlogById,
		{
			onSuccess: (response: any) => {
				if (response === "Error while getting attachment") {
					dispatchGlobalData({
						type: 'SET_SNACKBAR',
						payload: {
							snackbar: {
								message: t('translation:error.tryLater'),
								type: snackbarTypes.error,
								show: true,
								autoClose: true,
							}
						}
					});
					return;
				}

				setFilePreview({file: "data:image/png;base64," + response});
				setOpenPreviewAttach(true);
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	/** Preview dmg shapshot */
	const handleViewSnapshotFile = (e: React.SyntheticEvent, snapshotId: number) => {
		e.stopPropagation();
		fetchCheckOutInGetSnapshotBlogByIdMutation.mutate({snapshotId: snapshotId} as any);
		handleCloseMenu();
	}

	/** Preview event  */
	const handleViewAttachmentsFile = (e: React.SyntheticEvent, attach: any) => {
		e.stopPropagation();
		fetchCheckOutInGetAttachmentBlogByIdMutation.mutate({attachmentId: attach.id, type: "ATTACHMENT"} as any);
		handleCloseMenu();
	}

	/** Preview event  */
	const handleDeleteAttachmentsFile = (e: React.SyntheticEvent, attach: any) => {
		e.stopPropagation();

		setBufferDataActionEvent({
			action: 'deleteEventAttach',
			data: attach
		});
	}

	const handleClosePreview = () => {
		setFilePreview(null);
		setOpenPreviewSnapshot(false);
		setOpenPreviewAttach(false);
	}

	// Add module to form
	const handleAddEventModule = (e: React.BaseSyntheticEvent, eventId: number | null, moduleType: 'CAI' | 'DICHIARAZIONE_CLIENTE') => {
		e.stopPropagation();

		selectedEventIdUploadFile = eventId;
		selectedModuleCodeUpload = moduleType;

		if (!!inputTypeFileModuleRef) {
			// @ts-ignore
			inputTypeFileModuleRef.current.click();
		}
	}

	const handleModuleOnChange = async (e: React.BaseSyntheticEvent) => {
		if (!!e.target.files[0]?.size) {
			const resizedFile = await resizeImg(e.target.files[0]);
			setBufferDataActionEvent({
				action: 'addAttachments',
				data: {eventId: selectedEventIdUploadFile, documentType: selectedModuleCodeUpload, module: resizedFile}
			});

			// reset module settings
			selectedEventIdUploadFile = null;
			selectedModuleCodeUpload = null;
			inputTypeFileModuleRef.current.value  = '';
		}
	}

	const handleAddEventDamage = (e: React.SyntheticEvent, event: IEvent) => {
		e.stopPropagation();

		setNewEvent(event);
		setCreateEventMode(true);
	}

	const handleRemoveEventDamage = (e: React.SyntheticEvent, event: IEvent, damage: IDamage) => {
		e.stopPropagation();

		event.damages = event.damages.filter((dmg: IDamage) => dmg.id !== damage.id);

		setBufferDataActionEvent({
			action: 'associate',
			data: event
		});
	}

	const handleOpenMenu = (event: any , damage: any, type: 'evt' | 'evt-dmg' | 'dmg' | null) => {
		setAnchorEl(event.currentTarget);
		setSelectedDamage(damage);
		setMenuId(type);
	};

	// open module menu
	const handleOpen = (event: any, type: 'den' | 'cid' | null) => {
		setAnchorEl(event.currentTarget);
		setMenuId(type);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedDamage(null);
	};

	// @ts-ignore
	useEffect(() => {
		if (!!eventList) {
			remove();
			for(const event of eventList) {
				append({
					eventId: 		(event.id as number),
					counterpart: 	event.counterpart,
					counterpartData: {
						claimForm: 	!!event.claimForm ? event.claimForm : null, 	//(eventsFormValues.card?.[eventCardsInd].counterpartData.claimForm as string | null),
						cid: 		!!event.cid ? event.cid : null, 				//(eventsFormValues.card?.[eventCardsInd].counterpartData.cid as string | null),
						plate: 		!!event.plate ? event.plate : '', 				//(eventsFormValues.card?.[eventCardsInd].counterpartData.plate as string),
						date: 		!!event.date ? event.date : null, 				//(eventsFormValues.card?.[eventCardsInd].counterpartData.date as Dayjs | null),
						place: 		!!event.place ? event.place : '' 				//(eventsFormValues.card?.[eventCardsInd].counterpartData.place as string)
					}
				});
			}
		}
	}, [eventList]);

	const getEventLabel = useCallback((opt:any) => {
		const code = eventOptions.find((eo:any) => eo.id === opt.id)?.code

		return operationTranslation(`masterDataDamage.event.${code?.toLowerCase().replace('.', '_')}` as any)
	}, [eventOptions, i18n.language])

	return (
		<FormProvider {...eventsForm}>
			<Grid container columnSpacing={2} sx={{py: theme.spacing(2), borderTop: 1, borderBottom: 1 }}>
				{/*Event List*/}
				{
					!!eventList && fields.length > 0 &&
						eventList
							.map((event: IEvent, index: number) =>
								<Grid container item xs={12} sx={{mb: theme.spacing(2)}} id={`event-${event.id}`}>
									{/* Title */}
									<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
										<Typography variant={"body"}>
											{t("raCheckOutIn.checkDamage.event.title") + " "}
											<strong>
												{event.id}
											</strong>
										</Typography>
										<IconButton sx={{mx: theme.spacing(0.5)}} color={"primary"}
													onClick={(e: React.SyntheticEvent) => handleDeleteDamageRepairSheet(e, event)}>
											<DeleteRounded />
										</IconButton>
									</Grid>

									{/* Header*/}
									<Grid item xs={12}
										  sx={{display: "flex", justifyContent: "space-between",
												alignItems: "center",
												px: theme.spacing(0.5),
												border: "solid",
												borderColor: theme.palette.primary.main,
												bgcolor: theme.palette.primary.main,
												borderTopLeftRadius: 8,
												borderTopRightRadius: 8
											}}>
										<Typography variant={"body"} component={"div"} sx={{color: theme.palette.neutral.light}}>
											{getEventLabel(event.eventName)}
										</Typography>

										{
											event.counterpart &&
											<Chip label={
													<Typography variant={"h6"} component={"div"}>
														{t("raCheckOutIn.checkDamage.event.counterpart")}
													</Typography>
												}
											/>
										}
									</Grid>

									{/* Damages */}
									<Grid container item xs={12}
										  sx={{borderRight: 1, borderLeft: 1, borderBottom: 1,
											  borderColor: theme.palette.primary.main,
											  px: theme.spacing(0.5), pt: theme.spacing(0.5)}}>
										<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
											<Typography variant={"h6"} component={"div"}>
												{t("raCheckOutIn.checkDamage.event.damage")}
											</Typography>

											<IconButton color={"primary"} onClick={(e: React.SyntheticEvent) => handleAddEventDamage(e, event)}>
												<AddRounded />
											</IconButton>
										</Grid>

										{
											event.damages
												.map((damage: IDamage) =>
													<Grid container item key={`event-dmg-${damage.id}`} sx={{display: "flex", justifyContent: "space-between", alignItems: "center", my: theme.spacing(0.5)}}>
														<Grid item xs={4} sx={{display: "flex", alignItems: "center" }}>
															{t2(`masterDataDamage.partName.${damage?.part?.code}`) + (damage?.position?.code != "6" ?  " " + t2(`masterDataDamage.positionName.${damage?.position?.code}`) : "")}
														</Grid>
														<Grid item xs={4} sx={{display: "flex", alignItems: "center", justifyContent:'center'}}>
															<Chip label={operationTranslation(`damageList.severityValue.${damage.severity.toLowerCase()}` as any)}
																  sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />
														</Grid>
														<Grid item xs={4} zeroMinWidth sx={{display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
															{ !isMobile &&
																<>
																	{
																		( !!damage?.snapshotIdList && damage.snapshotIdList.length > 0 ) &&
																		damage.snapshotIdList
																			.map((snapshot: number) =>
																				<IconButton key={`event-dmg-view-desktop-${damage.id}-${snapshot}`}
																							color={"primary"} sx={{mx: theme.spacing(0.5)}}
																							onClick={(e: React.SyntheticEvent) => handleViewSnapshotFile(e, snapshot)}>
																					<VisibilityRounded />
																				</IconButton>
																			)
																	}
																	<IconButton color={"primary"} onClick={(e: React.SyntheticEvent) => handleRemoveEventDamage(e, event, damage)}>
																		<DeleteRounded />
																	</IconButton>
																</>
															}
															{/*eventList*/}
															{ isMobile &&
																<>
																	<Button color={"primary"}
																			sx={{mx: theme.spacing(0.5)}}
																			aria-controls={`event-menu-actions-${damage.id}`}
																			aria-haspopup="true"
																			size={"icon"}
																			aria-expanded={!!anchorEl && selectedDamage === damage && menuId === 'evt'}
																			onClick={(event) => handleOpenMenu(event, damage, 'evt')}>
																		<MoreVertRounded />
																	</Button>

																	<Menu
																		id={`event-menu-actions-${damage.id}`}
																		anchorEl={anchorEl}
																		open={!!anchorEl && selectedDamage === damage && menuId === 'evt'}
																		onClose={handleCloseMenu}
																	>

																		{( !!damage?.snapshotIdList && damage.snapshotIdList.length > 0 ) &&
																			damage.snapshotIdList
																				.map((snapshot: number) => (
																					<MenuItem
																						key={`event-dmg-menu-view-isMobile-${damage.id}-${snapshot}`}
																						onClick={(e: React.SyntheticEvent) => {
																								handleViewSnapshotFile(e, snapshot);
																						}}
																					>
																						<IconButton key={`event-dmg-menu-viewItem-isMobile-${damage.id}-${snapshot}`}
																									color="primary" sx={{mx: theme.spacing(0.5)}}>
																							<VisibilityRounded />
																						</IconButton>
																					</MenuItem>
																				))}

																		<MenuItem onClick={(e: React.SyntheticEvent) => handleRemoveEventDamage(e,event,damage)}>
																			<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																				<DeleteRounded />
																			</IconButton>
																		</MenuItem>
																	</Menu>
																</>
															}
														</Grid>
													</Grid>
												)
										}
									</Grid>

									{/*/!* Claim form *!/*/}
									{/*{*/}
									{/*	event.counterpart &&*/}
									{
										((['in', 'subIn'].includes(checkOutInMode) && event.counterpart) ||
											((checkOutInMode === 'oprOut' || checkOutInMode === 'oprIn' || checkOutInMode === 'ddt' || checkOutInMode === 'dataUpd') && event.counterpart)) &&
										<Grid container item xs={12}
											  sx={{borderRight: 1, borderLeft: 1, borderBottom: 1, borderColor: theme.palette.primary.main,
													p: theme.spacing(0.5)}}>
											<Grid item xs={12}>
												<Typography variant={"h6"} component={"div"}>
													{t("raCheckOutIn.checkDamage.event.module.den.title")}
												</Typography>
											</Grid>

											<Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
												<Button variant={"text"} color={"accent"}
														onClick={(e: React.SyntheticEvent) => handleAddEventModule(e, event.id, 'DICHIARAZIONE_CLIENTE')}
														disabled={!!event.claimForm}>
													{t("raCheckOutIn.checkDamage.event.module.den.add")}
												</Button>

												{
													!isMobile &&
													<>
														{
															!!event.claimForm &&
															<Box>
																<IconButton sx={{ml: theme.spacing(0.5)}} color={"primary"}
																			onClick={(e: React.SyntheticEvent) => {
																				handleViewAttachmentsFile(e, event.claimForm);
																			}}>
																	<VisibilityRounded />
																</IconButton>
																<IconButton sx={{ml: theme.spacing(0.5)}} color={"primary"}
																			onClick={(e: React.SyntheticEvent)=> handleDeleteAttachmentsFile(e, event.claimForm)}>
																	<DeleteRounded/>
																</IconButton>
															</Box>
														}
													</>
												}

												{/*claim*/}
												{
													isMobile &&
													!!event.claimForm &&
													<Box>
														<Button sx={{mx: theme.spacing(0.5)}}
																aria-controls={`event-den-menu-actions-${event.id}`}
																aria-haspopup="true"
																size={"icon"}
																color={"primary"}
																aria-expanded={!!anchorEl && menuId === 'den'}
																onClick={(event) => handleOpen(event, 'den')}>
															<MoreVertRounded />
														</Button>

														<Menu
															id={`event-claim-menu-actions-${event.id}`}
															anchorEl={anchorEl}
															open={!!anchorEl && menuId === 'den'}
															onClose={handleCloseMenu}
														>
															<MenuItem
																onClick={(e: React.SyntheticEvent) => {
																	handleCloseMenu();
																	handleViewAttachmentsFile(e, event.claimForm);
																}}
															>
																<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																	<VisibilityRounded />
																</IconButton>
															</MenuItem>

															<MenuItem onClick={(e: React.SyntheticEvent)=> handleDeleteAttachmentsFile(e, event.claimForm)}>
																<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																	<DeleteRounded />
																</IconButton>
															</MenuItem>

														</Menu>
													</Box>
												}

											</Grid>
										</Grid>
									}

									{/*/!* CID *!/*/}
									{/*{*/}
									{/*	event.counterpart &&*/}
									{
										((['in', 'subIn'].includes(checkOutInMode) && event.counterpart) ||
											((checkOutInMode === 'oprOut' || checkOutInMode === 'oprIn' || checkOutInMode === 'ddt' || checkOutInMode === 'dataUpd') && event.counterpart)) &&
											<Grid container item xs={12} sx={{borderRight: 1, borderLeft: 1, borderBottom: 1,
												borderColor: theme.palette.primary.main, p: theme.spacing(0.5)}}>
												<Grid item xs={12}>
													<Typography variant={"h6"} component={"div"}>
														{t("raCheckOutIn.checkDamage.event.module.cai.title")}
													</Typography>
												</Grid>

												<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
													<Button  variant={"text"} color={"accent"}
															 onClick={(e: React.SyntheticEvent) => handleAddEventModule(e, event.id, 'CAI')}
															 disabled={!!event.cid}>
														{t("raCheckOutIn.checkDamage.event.module.cai.add")}
													</Button>

													{   !isMobile &&
														!!event.cid &&
														<Box>
															<IconButton sx={{ml: theme.spacing(0.5)}} color={"primary"}
																		onClick={(e: React.SyntheticEvent) => {
																			handleViewAttachmentsFile(e, event.cid);
																		}}>
																<VisibilityRounded />
															</IconButton>
															<IconButton sx={{ml: theme.spacing(0.5)}} color={"primary"}
																		onClick={(e: React.SyntheticEvent) => handleDeleteAttachmentsFile(e, event.cid)}>
																<DeleteRounded />
															</IconButton>
														</Box>
													}

													{/*cid*/}
													{	isMobile &&
														!!event.cid &&
														<Box>
															<Button sx={{mx: theme.spacing(0.5)}}
																	aria-controls={`event-cid-menu-actions-${event.id}`}
																	aria-haspopup="true"
																	size={"icon"}
																	color={"primary"}
																	aria-expanded={!!anchorEl && menuId === 'cid'}
																	onClick={(event) => handleOpen(event, 'cid')}>
																<MoreVertRounded />
															</Button>

															<Menu
																id={`event-cid-menu-actions-${event.id}`}
																anchorEl={anchorEl}
																open={!!anchorEl && menuId === 'cid'}
																onClose={handleCloseMenu}
															>
																<MenuItem
																	onClick={(e: React.SyntheticEvent) => {
																		handleViewAttachmentsFile(e, event.cid);
																	}}
																>
																	<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																		<VisibilityRounded />
																	</IconButton>
																</MenuItem>

																<MenuItem onClick={(e: React.SyntheticEvent)=> handleDeleteAttachmentsFile(e, event.cid)}>
																	<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																		<DeleteRounded />
																	</IconButton>
																</MenuItem>

															</Menu>
														</Box>
													}
												</Grid>
											</Grid>
									}

									{/* Plate */}
									{
										((['in', 'subIn'].includes(checkOutInMode) && event.counterpart) ||
											((checkOutInMode === 'oprOut' || checkOutInMode === 'oprIn' || checkOutInMode === 'ddt' || checkOutInMode === 'dataUpd') && event.counterpart)) &&
										<>
											<Grid container item xs={12} sx={{
												borderRight: 1,
												borderLeft: 1,
												borderBottom: 1,
												borderColor: theme.palette.primary.main,
												px: theme.spacing(0.5),
												pt: theme.spacing(0.5)
											}}>
												<Grid item xs={12}>
													<Typography variant={"h6"} component={"div"}>
														{t("raCheckOutIn.checkDamage.event.module.counterpartPlate")}
													</Typography>
												</Grid>

												<Grid item xs={6} sx={{pr: theme.spacing(1)}}>
													<FormTextField<IEventCardForm>
														name={`card.${index}.counterpartData.plate`}
														control={eventsForm.control}
														label={t("raCheckOutIn.checkDamage.event.module.labelPlate")}
														fullWidth
													/>
												</Grid>
											</Grid>

											{/* Date */}
											<Grid container item xs={12} sx={{borderRight: 1, borderLeft: 1, borderBottom: 1, borderColor: theme.palette.primary.main,
												borderBottomLeftRadius: 8, borderBottomRightRadius: 8, px: theme.spacing(0.5)}}>
												<Grid item xs={12}>
													<Typography variant={"h6"} component={"div"}>
														{t("raCheckOutIn.checkDamage.event.module.dateAndPlace")}
													</Typography>
												</Grid>

												<Grid item xs={6} sx={{pr: theme.spacing(1)}}>
													<FormDateTimePicker<IEventCardForm>
														name={`card.${index}.counterpartData.date`}
														label={t("raCheckOutIn.checkDamage.event.module.labelDate")}
														control={eventsForm.control}
													/>
												</Grid>
												<Grid item xs={6} sx={{pl: theme.spacing(1)}}>
													<FormTextField<IEventCardForm>
														name={`card.${index}.counterpartData.place`}
														control={eventsForm.control}
														label={t("raCheckOutIn.checkDamage.event.module.labelPlace")}
														fullWidth
													/>
												</Grid>
											</Grid>
										</>
									}
								</Grid>
							)
				}

				{/* Damage List */}
				<Grid item xs={4} sx={{pb: theme.spacing(1), display: "flex"}}>
					<Typography variant={"h6"} sx={{pl: theme.spacing(1)}}>
						{t("raCheckOutIn.checkDamage.damageList.labelPart")}
					</Typography>
				</Grid>
				<Grid item xs={4} sx={{display: "flex",my: theme.spacing(1), alignItems:'center', justifyContent:'center'}} >
					<Typography variant={"h6"}>
						{t("raCheckOutIn.checkDamage.damageList.labelSeverity")}
					</Typography>
				</Grid>

				<Grid item xs={4} sx={{display:'flex', justifyContent:'flex-end'}}/>
					{
						damages
							.map((damage: IDamage, index: number) =>
								<Grid container item  key={`damage-${damage.id}`}
									  columnSpacing={2} sx={{display: "flex", justifyContent: "space-between", alignItems: "center", my: theme.spacing(0.5)}}>
									<Grid item xs={4} sx={{display: "flex", alignItems: "center"}}>
										{
											createEventMode &&
											<Checkbox color={"primary"}
													disabled={!(!!damage.snapshotIdList && damage.snapshotIdList.length === MAX_NUM_DAMAGE_ATTACH_FILES)}
												onChange={(e: React.SyntheticEvent, checked: boolean) => handleSelectEventDamage(e, checked, damage)}
											/>
										}

										<Typography variant={"body"} sx={{pl: theme.spacing(1)}}>
										{t2(`masterDataDamage.partName.${damage?.part?.code}`) + (damage?.position?.code != "6" ?  " " + t2(`masterDataDamage.positionName.${damage?.position?.code}`) : "")}
										</Typography>
									</Grid>
									<Grid item xs={4} sx={{display: "flex",my: theme.spacing(0.5), alignItems:'center', justifyContent:'center'}}>
										{
											<Chip label={operationTranslation(`damageList.severityValue.${damage.severity.toLowerCase()}` as any)} sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}}/>
										}
									</Grid>
									{/* Actions */}
									<Grid item xs={4} zeroMinWidth sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
										{!isMobile &&
											<Box>
												{/* add foto */}
												{
													(!readOnly && !!damage?.snapshotIdList && damage.snapshotIdList.length < MAX_NUM_DAMAGE_ATTACH_FILES) &&
													<IconButton color={"primary"}
																component="label"
																sx={{mx: theme.spacing(0.5)}}>
														<input hidden accept="image/*"
															type="file"
															ref={el => inputTypeFilesDmgSnapshotRef.current[index] = el}
															onChange={(e: React.BaseSyntheticEvent) => handleFileDmgSnapshotOnChange(e, damage, index)}
														/>
														<UploadRounded />
													</IconButton>
												}
												{
													!readOnly && !!damage?.snapshotIdList && damage.snapshotIdList.length >= MAX_NUM_DAMAGE_ATTACH_FILES &&
													<IconButton disabled sx={{mx: theme.spacing(0.5)}}>
														<UploadRounded />
													</IconButton>
												}

												{/* preview foto */}
												{
													( !!damage?.snapshotIdList && damage.snapshotIdList.length > 0 ) &&
													damage.snapshotIdList
														.map((snapshot: number, index: number) =>
															<IconButton key={`damage-view-desktop-${damage.id}-${index}`}
																		color={"primary"} sx={{mx: theme.spacing(0.5)}}
																		onClick={(e: React.SyntheticEvent) => handleViewSnapshotFile(e, snapshot)}>
																<VisibilityRounded />
															</IconButton>
													)
												}

												{
													!!damage?.snapshotIdList && damage.snapshotIdList.length < MAX_NUM_DAMAGE_ATTACH_FILES &&
													new Array(MAX_NUM_DAMAGE_ATTACH_FILES - damage.snapshotIdList.length)
														.fill(null)
														.map((elem: any, ind: number) =>
															<IconButton key={`damage-view-disabled-desktop-${damage.id}-${ind}`}
																		disabled
																		sx={{mx: theme.spacing(0.5)}}>
																<VisibilityRounded />
															</IconButton>
														)
												}

												{/* delete damage */}
												{
													!readOnly &&
													<IconButton color={"primary"} sx={{mx: theme.spacing(0.5)}}
																onClick={(e: React.SyntheticEvent) => handleDeleteDamage(e, damage)}>
														<DeleteRounded />
													</IconButton>
												}
											</Box>

										}

										{/*damagelist menu action*/}
										{
											isMobile &&
											<>
												<Button sx={{mx: theme.spacing(1)}}
														aria-controls={`damages-menu-actions-${damage.id}`}
														aria-haspopup="true"
														size={"icon"}
														color={'primary'}
														aria-expanded={!!anchorEl && selectedDamage === damage && menuId === 'dmg'}
														onClick={(event) => handleOpenMenu(event, damage, 'dmg')}>
													<MoreVertRounded />
												</Button>

												<Menu id={`damages-menu-actions-${damage.id}`}
														anchorEl={anchorEl}
														open={!!anchorEl && selectedDamage === damage && menuId === 'dmg'}
														onClose={handleCloseMenu}>
													{
														!readOnly && !!damage?.snapshotIdList && damage.snapshotIdList.length < MAX_NUM_DAMAGE_ATTACH_FILES &&
														<MenuItem>
															<IconButton color="primary"
																		component="label"
																		sx={{mx: theme.spacing(0.5)}}>
																<input hidden accept="image/*"
																		type="file"
																		ref={el => inputTypeFilesDmgSnapshotRef.current[index] = el}
																		onChange={(e: React.BaseSyntheticEvent) => handleFileDmgSnapshotOnChange(e, damage, index)}
																/>
																<UploadRounded />
															</IconButton>
														</MenuItem>
													}

													{
														!!damage?.snapshotIdList &&	damage.snapshotIdList.length > 0 &&
														damage.snapshotIdList
															.map((snapshot) =>
																<MenuItem key={`damage-menu-view-isMobile-${damage.id}-${snapshot}`}
																			onClick={(e: React.SyntheticEvent) => {
																				handleCloseMenu();
																				handleViewSnapshotFile(e, snapshot);
																			}}
																>
																	<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																		<VisibilityRounded />
																	</IconButton>
																</MenuItem>
														)
													}

													{
														!!damage?.snapshotIdList && damage?.snapshotIdList.length <= 2 &&
														new Array(MAX_NUM_DAMAGE_ATTACH_FILES - damage.snapshotIdList.length)
															.fill(null)
															.map((snapshot: any) =>
																<MenuItem key={`damage-menu-view-isMobile-disabled-${damage.id}-${snapshot}`}>
																	<IconButton disabled sx={{mx: theme.spacing(0.5)}}
																				onClick={(e: React.SyntheticEvent) => {
																					handleCloseMenu();
																					handleViewSnapshotFile(e, snapshot);
																				}}>
																		<VisibilityRounded />
																	</IconButton>
																</MenuItem>
															)
													}

													{
														!readOnly &&
														<MenuItem onClick={(e: React.SyntheticEvent)=> handleDeleteDamage(e,damage)}>
															<IconButton color="primary" sx={{mx: theme.spacing(0.5)}}>
																<DeleteRounded />
															</IconButton>
														</MenuItem>
													}
												</Menu>
											</>
										}
									</Grid>
						</Grid>
					)
					}
			</Grid>

			<Grid container>
				{
					!readOnly && !createEventMode &&
					<Grid item xs={6} sx={{mt: theme.spacing(1)}}>
						<Button variant={"text"} color={"secondary"} onClick={handleOpenAddDamage}>
							{t("raCheckOutIn.checkDamage.button.addDamage")}
						</Button>
					</Grid>

				}

				{
					!createEventMode && !readOnly && damages.length > 0 &&
					<Grid item xs={6} sx={{display: "flex", justifyContent: "flex-end", mt: theme.spacing(1)}}>
						<Button variant={"text"} color={"secondary"} onClick={handleCreateEvent}>
							{t("raCheckOutIn.checkDamage.button.createEvent")}
						</Button>
					</Grid>
				}

				{/*	Actions create event */}
				{
					!readOnly && createEventMode &&
					<>
						<Grid item xs={6} sx={{mt: theme.spacing(1)}}>
							<Button variant={"text"} color={"secondary"} onClick={handleEventAssociate}>
								{t("raCheckOutIn.checkDamage.button.associate")}
							</Button>
						</Grid>

						<Grid item xs={6} sx={{display: "flex", justifyContent: "flex-end", mt: theme.spacing(1)}}>
							<Button variant={"text"} color={'accent'} onClick={handleCreateEventCancel}>
								{t("raCheckOutIn.checkDamage.button.cancel")}
							</Button>
						</Grid>
					</>
				}
			</Grid>

			{/* Input per gestire inserimento della foto della targa */}
			{/*<input type={"file"} hidden ref={platePhotoRef}*/}
			{/*	   onChange={(e: React.BaseSyntheticEvent) => handlePlatePhotoOnChange(e)} />*/}

			{/* Dialog add new damage*/}
			{
				!readOnly &&
				<DamageSelector
					open={openDamageSelector}
					onClose={handleCloseAddDamage}
					damage={damageToUpdate}
				/>
			}

			{/* Dialog event selector*/}
			{
				!readOnly &&
				<EventSelector
					unavailableEvents={unavailableEvents}
					open={openCreateEventSelector}
					onClose={handleCloseEventSelector}
				/>
			}

			{/* Preview snapshots */}
			<Preview
				open={openPreviewSnapshot}
				attach={filePreview}
				onClose={handleClosePreview}
				deleteAction={!readOnly && handleDeleteFile}
			/>

			{/* Preview modules DEN && CAI */}
			<Preview
				open={openPreviewAttach}
				attach={filePreview}
				onClose={handleClosePreview}
			/>

			<input hidden accept="image/*" multiple type="file"
				   ref={inputTypeFileModuleRef}
				   onChange={(e: React.BaseSyntheticEvent) => handleModuleOnChange(e)} />

			<CustomLoader loading={fetchCheckOutInGetAttachmentBlogByIdMutation.isLoading || fetchCheckOutInGetSnapshotBlogByIdMutation.isLoading} />
		</FormProvider>
	);
})

export default DamageList;